// lib/auth-token.ts
const TOKEN_STORAGE_KEY = "authToken"

type JwtPayload = {
  exp?: number
  [key: string]: unknown
}

/**
 * Read the raw token from localStorage (browser only).
 */
export function getStoredToken(): string | null {
  if (typeof window === "undefined") return null
  return window.localStorage.getItem(TOKEN_STORAGE_KEY)
}

/**
 * Persist the token in localStorage (browser only).
 */
export function storeToken(token: string) {
  if (typeof window === "undefined") return
  window.localStorage.setItem(TOKEN_STORAGE_KEY, token)
}

/**
 * Remove any stored token from localStorage (browser only).
 */
export function clearStoredToken() {
  if (typeof window === "undefined") return
  window.localStorage.removeItem(TOKEN_STORAGE_KEY)
}

/**
 * Best-effort JWT parsing. If the token cannot be parsed, null is returned.
 * We only attempt this on the client.
 */
function parseJwt(token: string): JwtPayload | null {
  if (typeof window === "undefined") return null

  try {
    const [, payload] = token.split(".")
    if (!payload) return null

    // Handle base64url format
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/")
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=")

    const json = window.atob(padded)
    return JSON.parse(json) as JwtPayload
  } catch {
    return null
  }
}

/**
 * Returns true when the token has a valid `exp` claim and is expired.
 * On the server we always return false and let the API handle expiry.
 */
export function isTokenExpired(token: string): boolean {
  if (typeof window === "undefined") return false

  const payload = parseJwt(token)
  if (!payload?.exp) {
    // No exp claim – treat as non-expiring on the client,
    // the API will still enforce its own rules.
    return false
  }

  const nowInSeconds = Math.floor(Date.now() / 1000)
  return payload.exp <= nowInSeconds
}

export interface Session {
  id?: number
  email?: string
  role?: string
  expiresAt?: Date
}

/**
 * Decodes the current token's claims for display purposes (user menu etc.).
 * Never use this for authorization decisions; the API enforces those.
 */
export function getSession(): Session | null {
  const token = getValidToken()
  if (!token) return null

  const payload = parseJwt(token)
  if (!payload) return {}

  return {
    id: typeof payload.id === "number" ? payload.id : undefined,
    email: typeof payload.email === "string" ? payload.email : undefined,
    role: typeof payload.role === "string" ? payload.role : undefined,
    expiresAt: payload.exp ? new Date(payload.exp * 1000) : undefined,
  }
}

/**
 * Returns the token if it's present and not expired.
 * If the token is expired, it is removed from storage and `null` is returned.
 */
export function getValidToken(): string | null {
  const token = getStoredToken()
  if (!token) return null

  if (isTokenExpired(token)) {
    clearStoredToken()
    return null
  }

  return token
}
